// Doc section 7 "Scroll behaviour": milestones fire once per page view,
// no more than one scroll-driven callback per animation frame.

const MILESTONES = [10, 25, 50, 75, 90, 100] as const;
export type ScrollMilestone = (typeof MILESTONES)[number];

export function trackScroll(onMilestone: (milestone: ScrollMilestone, maxScrollPercent: number) => void): {
  getMaxScrollPercent: () => number;
  stop: () => void;
} {
  const fired = new Set<ScrollMilestone>();
  let maxScrollPercent = 0;
  let ticking = false;

  function computePercent(): number {
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop || 0;
    const viewportHeight = window.innerHeight || doc.clientHeight;
    const documentHeight = Math.max(doc.scrollHeight, document.body?.scrollHeight ?? 0);
    if (documentHeight <= viewportHeight) return 100; // no scrollable content — page is "fully seen"
    const percent = ((scrollTop + viewportHeight) / documentHeight) * 100;
    return Math.min(100, Math.max(0, percent));
  }

  function onScroll(): void {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const percent = computePercent();
      if (percent > maxScrollPercent) maxScrollPercent = percent;
      for (const milestone of MILESTONES) {
        if (percent >= milestone && !fired.has(milestone)) {
          fired.add(milestone);
          onMilestone(milestone, maxScrollPercent);
        }
      }
      ticking = false;
    });
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  // Fire once at load in case the page loads already scrolled (deep link with #anchor) or is short.
  onScroll();

  return {
    getMaxScrollPercent: () => maxScrollPercent,
    stop: () => window.removeEventListener("scroll", onScroll),
  };
}
